import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  addIgnoredBeaconAsync,
  selectIgnoreList,
  selectPriorities,
} from '../../../redux/locatorDrift/locatorDriftSlice';
import {
  explainBeaconDetail,
  explainBeaconImpact,
  getPriority,
  isBeaconIgnored,
} from '../../../utilities/locatorDrift/locatorFormatters';
import BeaconDetails from './BeaconDetails';
import { analyticsTableStyles } from './analyticsDriftStyles';

export default function AnalyticsTable({ category, allItems, expandedIndex, setExpandedIndex }) {
  const dispatch = useDispatch();
  const ignoreList = useSelector(selectIgnoreList);
  const priorities = useSelector(selectPriorities);

  return (
    <Accordion defaultExpanded={category.defaultOpen} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color={category.color} label={category.label} />
          <Typography variant="body2" color="text.secondary">
            {category.items.length}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={analyticsTableStyles.details}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Beacon ID</TableCell>
              <TableCell>Event Name</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Ver A</TableCell>
              <TableCell>Ver B</TableCell>
              <TableCell>Impact</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {category.items.map((item) => {
              const globalIndex = allItems.indexOf(item);
              const isOpen = expandedIndex === globalIndex;
              const ignored = isBeaconIgnored(ignoreList, item.beacon_id);
              const versionA = item.version_a || item.a;
              const versionB = item.version_b || item.b;
              const priority = getPriority(priorities, item.beacon_id);
              return (
                <React.Fragment key={`${globalIndex}-${item.beacon_id}`}>
                  <TableRow
                    hover
                    sx={analyticsTableStyles.clickableRow(ignored)}
                    onClick={() => setExpandedIndex(isOpen ? null : globalIndex)}
                  >
                    <TableCell>
                      <Chip
                        size="small"
                        color={category.color}
                        label={(item.status || item.drift || '').toUpperCase()}
                      />
                    </TableCell>
                    <TableCell>{item.beacon_id}</TableCell>
                    <TableCell>{item.event_name || 'Unknown'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={priority}
                        color={
                          priority === 'P0' ? 'error' : priority === 'P1' ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {versionA ? (
                        <Chip
                          size="small"
                          color={versionA.found ? 'success' : 'error'}
                          label={versionA.found ? 'FOUND' : 'NOT FOUND'}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {versionB ? (
                        <Chip
                          size="small"
                          color={versionB.found ? 'success' : 'error'}
                          label={versionB.found ? 'FOUND' : 'NOT FOUND'}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={analyticsTableStyles.impactCell}>
                      {explainBeaconImpact(item)}
                    </TableCell>
                    <TableCell>
                      {!ignored && (
                        <Button
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            dispatch(addIgnoredBeaconAsync(item.beacon_id));
                          }}
                        >
                          Ignore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell colSpan={8} sx={analyticsTableStyles.detailCell}>
                        <Alert
                          severity={category.color === 'error' ? 'error' : 'info'}
                          sx={analyticsTableStyles.detailAlert}
                        >
                          {explainBeaconDetail(item, priority)}
                        </Alert>
                        <BeaconDetails item={item} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}
